<script setup lang="ts">
/**
 * Non-blocking banner shown when a new app version is deployed.
 *
 * Polls /version.txt and compares to the build ID compiled into the bundle.
 * Dismissal is sticky per-build via sessionStorage so it doesn't reappear
 * for the same version after the user clicks "Later".
 */
import { computed, ref } from 'vue'
import { useVersionCheck } from '@/composables/useVersionCheck'

const { updateAvailable, refreshNow, remoteBuildId } = useVersionCheck()

const DISMISS_KEY = 'claimshield:update-banner:dismissed-build-id'

const dismissedBuildId = ref<string | null>(
  typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(DISMISS_KEY) : null,
)

const showBanner = computed(
  () =>
    updateAvailable.value &&
    remoteBuildId.value !== null &&
    remoteBuildId.value !== dismissedBuildId.value,
)

function dismiss() {
  if (remoteBuildId.value) {
    dismissedBuildId.value = remoteBuildId.value
    try {
      sessionStorage.setItem(DISMISS_KEY, remoteBuildId.value)
    } catch {
      // sessionStorage unavailable (private mode, etc.) — fall back to in-memory dismiss
    }
  }
}
</script>

<template>
  <Transition name="banner-slide">
    <div
      v-if="showBanner"
      class="fixed top-0 inset-x-0 z-50 bg-primary-700 text-white shadow-md"
      role="status"
      aria-live="polite"
    >
      <div class="max-w-5xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
        <p class="text-sm font-medium">
          A new version is available. Your progress is saved — refresh when ready.
        </p>
        <div class="flex items-center gap-2 shrink-0">
          <button
            type="button"
            class="text-sm font-medium underline hover:no-underline"
            @click="dismiss"
          >
            Later
          </button>
          <button
            type="button"
            class="text-sm font-semibold bg-white text-primary-700 px-3 py-1 rounded hover:bg-primary-50"
            @click="refreshNow"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}
.banner-slide-enter-from,
.banner-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
